import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import { TWclassNames } from "./Div";

/**
 * Modern inline picker for table editing.
 * Renders dropdown in a Portal to avoid layout/overflow issues inside tables.
 * Auto-opens on mount for quick selection.
 *
 * @param {Object} props
 * @param {Array<{value: string, label: string, class?: string}>} props.options
 * @param {string} props.value - Current value
 * @param {function} props.onChange - (value) => void
 * @param {function} props.onClose - Called when picker closes (e.g. on blur)
 * @param {string} [props.placeholder] - Placeholder when empty
 * @param {string} [props.className] - Additional class for trigger
 */
export const InlineTablePicker = ({
  options,
  value,
  onChange,
  onClose,
  placeholder = "–",
  className = "",
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options.find((o) => o.value === value || o.value === value?.toString());

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: Math.max(rect.width, 176),
      });
    }
  };

  useLayoutEffect(() => {
    updatePosition();
    setIsOpen(true);
    return () => setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    onClose?.();
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") handleClose();
  };

  const dropdownContent = isOpen && (
    <>
      <div
        className="fixed inset-0 z-[9998]"
        onClick={handleClose}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Close"
      />
      <div
        className="fixed z-[9999] min-w-[176px] max-h-48 overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5"
        style={{
          top: position.top + 4,
          left: position.left,
          width: position.width,
        }}
        role="listbox"
        onKeyDown={handleKeyDown}
      >
        {options.map((option) => {
          const isSelected = option.value === value || option.value === value?.toString();
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => handleSelect(option.value)}
              className={TWclassNames(
                "flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50",
                isSelected && "bg-gray-50"
              )}
            >
              <span
                className={TWclassNames(
                  option.class || "bg-gray-100 text-gray-800",
                  "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium",
                  isSelected && "ring-2 ring-blue-500 ring-offset-1"
                )}
              >
                {t(option.label)}
              </span>
              {isSelected && (
                <CheckIcon className="h-4 w-4 flex-shrink-0 text-blue-600" />
              )}
            </button>
          );
        })}
      </div>
    </>
  );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={TWclassNames(
          "inline-flex items-center gap-1 rounded-lg border border-transparent px-2.5 py-1.5 text-left text-sm transition-all",
          "hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
          "min-w-[100px]",
          className
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {selectedOption ? (
          <span
            className={TWclassNames(
              selectedOption.class || "bg-gray-100 text-gray-800",
              "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
            )}
          >
            {t(selectedOption.label)}
          </span>
        ) : (
          <span className="text-gray-500">{placeholder}</span>
        )}
        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
      </button>
      {typeof document !== "undefined" &&
        createPortal(dropdownContent, document.body)}
    </>
  );
};
